import { createEntityAdapter, EntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, createSelector, on } from '@ngrx/store';
import { PdfTitleImagesActions } from './pdf-title-images.actions';
import { LoadingState } from '../../../../core/models/common';
import {PdfTitleImageDto} from "@travel-planner/library-travel-planner-api";

export interface PdfTitleImagesState extends EntityState<PdfTitleImageDto> {
    loadingState: LoadingState;
    totalPdfTitleImages: number | null;
    loadingEntityIds: (number | string)[];
    scrollPosition: number;

    [key: string]: any;
}

export const pdfTitleImagesAdapter: EntityAdapter<PdfTitleImageDto> = createEntityAdapter<PdfTitleImageDto>();

const initialState: PdfTitleImagesState = pdfTitleImagesAdapter.getInitialState({
    loadingState: 'Initial',
    totalPdfTitleImages: null,
    loadingEntityIds: [],
    scrollPosition: 0
});

export const pdfTitleImagesFeature = createFeature({
    name: 'pdfTitleImages',
    reducer: createReducer(
        initialState,
        on(PdfTitleImagesActions.loadPdfTitleImages, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(PdfTitleImagesActions.loadPdfTitleImage, (state) => ({
            ...state,
            loadingState: 'Loading' as const
        })),
        on(PdfTitleImagesActions.loadPdfTitleImagesSuccess, (state, { pdfTitleImages, total }) => {
            if (total != null) {
                return {
                    ...pdfTitleImagesAdapter.setAll(pdfTitleImages, state),
                    totalPdfTitleImages: total,
                    loadingState: 'Loaded' as const,
                    scrollPosition: 0
                };
            }
            return {
                ...pdfTitleImagesAdapter.addMany(pdfTitleImages, state),
                loadingState: 'Loaded' as const
            };
        }),
        on(PdfTitleImagesActions.scrollPdfTitleImagesList, (state, { scrollPosition }) => {
            return {
                ...state,
                scrollPosition: scrollPosition
            };
        }),
        on(PdfTitleImagesActions.loadPdfTitleImageSuccess, (state, { pdfTitleImage }) => {
            return {
                ...pdfTitleImagesAdapter.setOne(pdfTitleImage, state),
                loadingState: 'Loaded' as const
            };
        }),
        on(PdfTitleImagesActions.addPdfTitleImage, (state) => {
            const loadingEntityIds = [...state.loadingEntityIds, 'new-pdf-title-image'];
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(PdfTitleImagesActions.addPdfTitleImageSuccess, (state, { pdfTitleImage }) => {
            const loadingEntityIds = [...state.loadingEntityIds].filter(id => id !== 'new-pdf-title-image');
            return {
                ...pdfTitleImagesAdapter.addOne(pdfTitleImage, state),
                loadingEntityIds: loadingEntityIds
            };
        }),
        on(PdfTitleImagesActions.updatePdfTitleImage, (state, { id }) => {
            const loadingEntityIds = [...state.loadingEntityIds, id];
            return { ...state, loadingEntityIds };
        }),
        on(PdfTitleImagesActions.updatePdfTitleImageSuccess, (state, { pdfTitleImage }) => {
            const loadingEntityIds = state.loadingEntityIds.filter(id => id !== pdfTitleImage.id);
            return {
                ...pdfTitleImagesAdapter.setOne(pdfTitleImage, state),
                loadingEntityIds
            };
        }),
        on(PdfTitleImagesActions.deletePdfTitleImage, (state, { id }) => {
            const loadingEntityIds = [...state.loadingEntityIds, id];
            return { ...state, loadingEntityIds };
        }),
        on(PdfTitleImagesActions.deletePdfTitleImageSuccess, (state, { id }) => {
            const loadingEntityIds = state.loadingEntityIds.filter(entityId => entityId !== id);
            return {
                ...pdfTitleImagesAdapter.removeOne(id, state),
                loadingEntityIds
            };
        }),
        on(PdfTitleImagesActions.uploadImage, (state, { id }) => {
            if (state.loadingEntityIds.includes(id)) { return state; }
            const loadingEntityIds = [...state.loadingEntityIds, id];
            return { ...state, loadingEntityIds: loadingEntityIds };
        }),
        on(PdfTitleImagesActions.uploadImageSuccess, (state, { pdfTitleImage }) => {
            const loadingEntityIds = state.loadingEntityIds.filter(id => id !== pdfTitleImage.id);
            return {
                ...pdfTitleImagesAdapter.setOne(pdfTitleImage, state),
                loadingEntityIds
            };
        }),
        on(PdfTitleImagesActions.updateFormControl, (state, { property, value }) => {
            return {
                ...state,
                [property]: value
            };
        }),
        on(
            PdfTitleImagesActions.loadPdfTitleImagesFailure,
            (state) => ({
                ...state,
                loadingState: 'Error' as const,
            })
        ),
        on(PdfTitleImagesActions.loadPdfTitleImageFailure, (state, { id }) => ({
            ...state,
            loadingEntityIds: state.loadingEntityIds.filter(itemId => itemId !== id),
            loadingState: 'Error' as const,
        })),
        on(PdfTitleImagesActions.addPdfTitleImageFailure, (state) => ({
            ...state,
            loadingEntityIds: state.loadingEntityIds.filter(id => id !== 'new-pdf-title-image'),
            loadingState: 'Error' as const,
        })),
        on(
            PdfTitleImagesActions.updatePdfTitleImageFailure,
            PdfTitleImagesActions.deletePdfTitleImageFailure,
            PdfTitleImagesActions.uploadImageFailure,
            (state, { id }) => ({
                ...state,
                loadingEntityIds: state.loadingEntityIds.filter(itemId => itemId !== id),
                loadingState: 'Error' as const,
            })
        ),
    )
});

const {
    name,
    reducer,
    selectPdfTitleImagesState,
} = pdfTitleImagesFeature;

const selectSafePdfTitleImagesState = createSelector(
    selectPdfTitleImagesState,
    (state): PdfTitleImagesState => state ?? initialState
);

const {
    selectEntities,
    selectAll,
    selectTotal
} = pdfTitleImagesAdapter.getSelectors(selectSafePdfTitleImagesState);

export const selectPdfTitleImagesLoadingState = createSelector(
    selectSafePdfTitleImagesState,
    (state) => state.loadingState
);

export const selectPdfTitleImageLoadingState = (id: number | string) =>
    createSelector(selectSafePdfTitleImagesState, (state: PdfTitleImagesState) => state.loadingEntityIds.includes(id));

export const selectPdfTitleImagesListScrollPosition = createSelector(
    selectSafePdfTitleImagesState,
    (state: PdfTitleImagesState) => state.scrollPosition
);

export const selectFormValue = (property: string) =>
    createSelector(selectSafePdfTitleImagesState, (state: PdfTitleImagesState) => state[property]);

export const selectPdfTitleImages = selectAll;

export const selectPdfTitleImagesEntities = selectEntities;

export const selectPdfTitleImage = (id: number) =>
    createSelector(selectSafePdfTitleImagesState, (state) => state.entities[id] || null);

export const selectPdfTitleImagesLoadedCount = selectTotal;

export const selectPdfTitleImagesTotalPdfTitleImages = createSelector(
    selectSafePdfTitleImagesState,
    (state: PdfTitleImagesState) => state.totalPdfTitleImages
);
